import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createTeleport } from '../../../common/Teleport';
import { useStatefulScrollLock } from '../../../common/hooks/useStatefulScrollLock';
import { ChildrenOnly } from '../../../common/types/ChildrenOnly';
import { returnVoid } from '../../../common/util/returnVoid';

const { Input: OverlayInput, Output: OverlayOutput, Coordinator } = createTeleport();

interface OverlayState {
  activeId: string | null;
  x: number;
  y: number;
  fixedMobileHeight: boolean;
}

const defaultState: OverlayState = { x: 0, y: 0, activeId: null, fixedMobileHeight: false };

interface OverlayContext extends OverlayState {
  activeId: string | null;
  x: number;
  y: number;
  clear: VoidFunction;
  activate: (id: string, x: number, y: number, fixedMobileHeight?: boolean) => void;
}

const defaultContext: OverlayContext = {
  ...defaultState,
  clear: returnVoid,
  activate: returnVoid,
};

const OverlayContext = createContext(defaultContext);

export const useOverlay = () => useContext(OverlayContext);

export const OverlayProvider = ({ children }: ChildrenOnly) => {
  const [state, setState] = useState(defaultState);
  const { setLocked } = useStatefulScrollLock();

  const activate = useCallback(
    (activeId: string, x: number, y: number, fixedMobileHeight: boolean = false) => {
      setState({ activeId, x, y, fixedMobileHeight });
      setLocked(true);
    },
    [setLocked],
  );

  const clear = useCallback(() => {
    setState(defaultState);
    setLocked(false);
  }, [setLocked]);

  const contextValue = useMemo(() => ({ ...state, activate, clear }), [activate, clear, state]);

  return (
    <OverlayContext.Provider value={contextValue}>
      <Coordinator>{children}</Coordinator>
    </OverlayContext.Provider>
  );
};

export { OverlayInput, OverlayOutput };
